// Spares Audit — the only place this screen talks to the server.
// Contract: docs/BACKEND-REQUEST-spares-audit.md §2.
//
// DEV-ONLY MOCK: when REACT_APP_SPARES_AUDIT_MOCK=true (set in the git-ignored
// .env.local) AND this is not a production build, calls go to ./mockApi instead.
// The mock is never chosen automatically - if the real endpoint is missing the
// screen shows the server error, it does not fall back to sample data.
import axios from 'util/Api'
import * as FileSaver from 'file-saver'

export const USE_MOCK = process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_SPARES_AUDIT_MOCK === 'true'

let mockModule = null
const mock = () => {
  if (!mockModule) mockModule = import('./mockApi').then(m => m.default)
  return mockModule
}

const BASE = 'spare-audit'

// Arrays go as comma-separated ids; empty values are dropped so the server sees
// "no filter" rather than an empty string.
export const toParams = (f) => {
  const p = {}
  Object.keys(f || {}).forEach(k => {
    const v = f[k]
    if (v === null || v === undefined || v === '') return
    if (Array.isArray(v)) { if (v.length) p[k] = v.join(','); return }
    p[k] = v
  })
  return p
}

const data = (r) => r.data

// A readable message for any failure. 404 on our own routes means the backend
// for this screen is not deployed on this server yet - say exactly that.
export const errorText = (err) => {
  const res = err && err.response
  if (res && res.status === 404 && res.config && /spare-audit/.test(res.config.url || '')) {
    return 'Spares Audit API is not available on this server yet (404 on ' + res.config.url + ').'
  }
  if (res && (res.status === 403 || (res.status === 401 && res.data && /permission/i.test(String(res.data.message || ''))))) {
    return 'You do not have permission for this action (Spares Audit).'
  }
  if (res && res.data && res.data.message) return String(res.data.message)
  return (err && err.message) || 'Request failed'
}
export const errorCode = (err) => {
  const res = err && err.response
  return (res && res.data && (res.data.code || res.data.error)) || null
}

// GET queue: the whole screen (rows + counts + check counts) in one call.
export const getQueue = (filters) => (USE_MOCK
  ? mock().then(m => m.getQueue(filters))
  : axios.get(BASE + '/queue', { params: toParams(filters) }).then(data))

// GET detail: history + what changed since the audit, plus the fresh row.
export const getVoucher = (type, id) => (USE_MOCK
  ? mock().then(m => m.getVoucher(type, id))
  : axios.get(BASE + '/voucher/' + encodeURIComponent(type) + '/' + encodeURIComponent(id)).then(data))

export const auditVoucher = (body) => (USE_MOCK
  ? mock().then(m => m.audit(body))
  : axios.post(BASE + '/audit', body).then(data))

export const raiseQuery = (body) => (USE_MOCK
  ? mock().then(m => m.query(body))
  : axios.post(BASE + '/query', body).then(data))

export const reopenAudit = (body) => (USE_MOCK
  ? mock().then(m => m.reopen(body))
  : axios.post(BASE + '/reopen', body).then(data))

// Bulk on an explicit selection (max 200, synchronous).
export const bulkAudit = (body) => (USE_MOCK
  ? mock().then(m => m.bulkAudit(body))
  : axios.post(BASE + '/bulk-audit', body).then(data))

// Bulk on every eligible voucher matching the filters (backlog catch-up).
// Runs as a background batch on the server; poll getBatch until it finishes.
export const bulkAuditByFilter = (body) => (USE_MOCK
  ? mock().then(m => m.bulkAuditByFilter(body))
  : axios.post(BASE + '/bulk-audit-by-filter', body).then(data))

export const getBatch = (batchId) => (USE_MOCK
  ? mock().then(m => m.getBatch(batchId))
  : axios.get(BASE + '/batch/' + encodeURIComponent(batchId)).then(data))

// Users an Audit Query can be assigned to: any active user of the organisation.
// Stable reference data: fetched once per screen visit, when the query window first opens.
export const getAssignableUsers = () => (USE_MOCK
  ? mock().then(m => m.getAssignableUsers())
  : axios.get(BASE + '/assignable-users').then(data))

export const getEvents = (filters) => (USE_MOCK
  ? mock().then(m => m.getEvents(filters))
  : axios.get(BASE + '/events', { params: toParams(filters) }).then(data))

// Server-side CSV export so the file covers every matching voucher, not just
// the rows loaded on screen.
export const exportQueue = (filters) => (USE_MOCK
  ? mock().then(m => m.exportQueue(filters))
  : axios.get(BASE + '/export', { params: toParams(filters), responseType: 'blob' })
    .then(r => FileSaver.saveAs(r.data, 'spares-audit-' + Date.now() + '.csv')))
